import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button, Spin, Table, message} from 'antd';
import {LogoutOutlined} from '@ant-design/icons';
import moment from 'moment';

import logo from '../../assets/icon/l_logo.png';
import {fetchActiveApkList} from './apkApi';
import {clearApkToken} from './apkAuth';
import './styles.less';

const CDN_BASE =
  process.env.REACT_APP_cdn ||
  'https://ubldev.blob.core.windows.net/ubl-web-uploads/';

const buildDownloadUrl = (urlPath = '') => {
  if (/^https?:\/\//i.test(urlPath)) return urlPath;
  const base = CDN_BASE.endsWith('/') ? CDN_BASE : `${CDN_BASE}/`;
  const path = urlPath.startsWith('/') ? urlPath.slice(1) : urlPath;
  return `${base}${path}`;
};

const ApkListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadApks = async () => {
    setLoading(true);
    try {
      const res = await fetchActiveApkList();
      setRows(res?.data?.data || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        clearApkToken();
        navigate('/apk-distribution/login', {replace: true});
        return;
      }
      message.error(
        err?.response?.data?.message || 'Failed to load APK list',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApks();
  }, []);

  const handleLogout = () => {
    clearApkToken();
    navigate('/apk-distribution/login', {replace: true});
  };

  const handleDownload = (record) => {
    const href = buildDownloadUrl(record.url);
    const link = document.createElement('a');
    link.href = href;
    link.download = `${record.name || 'app'}.apk`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: 'APK Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Platform',
      dataIndex: ['platformInfo', 'display_label'],
      key: 'platform',
      render: (value) => value || '-',
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Release Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => (value ? moment(value).format('LLL') : '-'),
    },
    {
      title: 'Action',
      key: 'action',
      ...(isMobile ? {fixed: 'right', width: 10} : {}),
      render: (_, record) => (
        <Button
          className='apk-dist__download-btn'
          onClick={() => handleDownload(record)}>
          Download
        </Button>
      ),
    },
  ];

  return (
    <div className='apk-dist'>
      <div className='apk-dist__navbar'>
        <img src={logo} alt='Univision' />
        <Button onClick={handleLogout} icon={<LogoutOutlined />}>
          Logout
        </Button>
      </div>

      <div className='apk-dist__content'>
        <Spin spinning={loading}>
          <Table
            className='apk-dist__table'
            columns={columns}
            dataSource={rows}
            rowKey={(row) => row.id}
            pagination={false}
            // pagination={{pageSize: 10, showSizeChanger: false}}
            scroll={isMobile ? {x: 'max-content'} : undefined}
          />
        </Spin>
      </div>
    </div>
  );
};

export default ApkListPage;
